import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useEffect } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import ComboWizard from '../shared/combo-wizard'
import { ComboWizardData } from '../shared/combo-wizard-props'
import { COMBO_MAX_STEPS, COMBO_MIN_STEPS } from '../shared/combo-types'
import { ButtonNormal } from '../../../components/buttons/button-normal'
import {getZodiac, isEmpty, formatSignAndAge} from '../../../utils/functions'
import {
    buildFlexibleSteps,
    createFlexibleInterpretStep,
    FLEXIBLE_COMBO_NAME,
} from './functions'
import { FLEXIBLE_SPREADS, getFlexibleSpread } from './spread-registry'

const emptyClient = (): ComboWizardData => ({
    consultante: { name: '', date: '', zodiac: '', phone: '', personality: '' },
    consultado: { name: '', date: '', zodiac: '' },
    consulta: '',
    tipo: '',
})

const FlexibleCombo = () => {
    const navigate = useNavigate()
    const { user } = useSelector((state: any) => state)
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [started, setStarted] = useState(false)
    const [clientDraft, setClientDraft] = useState<ComboWizardData>(emptyClient)

    useEffect(() => {
        if (Object.entries(user).length === 0) {
            navigate('/')
        }
    }, [user, navigate])

    const toggleSpread = (id: string) => {
        setSelectedIds((prev) => {
            if (prev.includes(id)) {
                return prev.filter((x) => x !== id)
            }
            if (prev.length >= COMBO_MAX_STEPS) {
                return prev
            }
            return [...prev, id]
        })
    }

    const move = (index: number, dir: -1 | 1) => {
        setSelectedIds((prev) => {
            const next = [...prev]
            const target = index + dir
            if (target < 0 || target >= next.length) return prev
            ;[next[index], next[target]] = [next[target], next[index]]
            return next
        })
    }

    const canStart =
        selectedIds.length >= COMBO_MIN_STEPS && selectedIds.length <= COMBO_MAX_STEPS

    const spreads = useMemo(
        () => selectedIds.map((id) => getFlexibleSpread(id)!).filter(Boolean),
        [selectedIds]
    )

    const steps = useMemo(() => buildFlexibleSteps(selectedIds), [selectedIds])
    const interpretStep = useMemo(() => createFlexibleInterpretStep(spreads), [spreads])

    const handleClientNameDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target
        if (name === 'date' && parseInt(value.slice(0, 4)) > 1900) {
            setClientDraft((prev) => ({
                ...prev,
                consultante: {
                    ...prev.consultante,
                    date: value,
                    zodiac: getZodiac(value),
                },
            }))
            return
        }
        setClientDraft((prev) => ({
            ...prev,
            consultante: { ...prev.consultante, [name]: value },
        }))
    }

    const copiar = () => {
        localStorage.setItem('client', JSON.stringify(clientDraft.consultante))
    }

    const pegar = () => {
        const cliente = localStorage.getItem('client')
        if (!cliente) return
        const clienteObj = JSON.parse(cliente)
        setClientDraft((prev) => ({
            ...prev,
            consultante: {
                name: clienteObj.name || '',
                date: clienteObj.date || '',
                zodiac: clienteObj.zodiac || '',
                phone: clienteObj.phone || '',
            },
        }))
    }

    const volverAlPicker = (data?: ComboWizardData) => {
        if (data) {
            setClientDraft({
                ...emptyClient(),
                ...data,
                consultante: { ...emptyClient().consultante, ...data.consultante },
                consultado: { ...emptyClient().consultado!, ...data.consultado },
                consulta: '',
                tipo: '',
            })
        }
        setStarted(false)
    }

    if (Object.entries(user).length === 0 || isEmpty(localStorage.getItem('personality'))) {
        return null
    }

    if (started && canStart) {
        return (
            <ComboWizard
                key={selectedIds.join('-')}
                comboName={FLEXIBLE_COMBO_NAME}
                steps={steps}
                bgClass="bg-general"
                interpretStep={interpretStep}
                allowAnyLevel
                onNuevoCombo={volverAlPicker}
                initialData={clientDraft}
                consultaPlaceholder="Consulta o inquietud para esta tirada (opcional)."
            />
        )
    }

    return (
        <div className="relative w-full min-h-screen bg-no-repeat bg-cover bg-general mini:px-2 md:px-4 py-6">
            <p className="w-full pb-2 italic font-bold text-center text-white mini:text-xl xl:text-4xl drop-shadow-lg">
                Combo de lecturas
            </p>
            <p className="pb-6 text-center text-white mini:text-sm xl:text-base drop-shadow-md">
                Cargá el consultante, elegí entre {COMBO_MIN_STEPS} y {COMBO_MAX_STEPS} tiradas y el
                orden. Los datos se mantienen al comenzar el combo.
            </p>

            <div className="max-w-4xl mx-auto mb-4 p-4 rounded-lg bg-violetLight/70 shadow-card-horizontal">
                <p className="mb-3 font-semibold text-white drop-shadow">Datos del consultante</p>
                <div className="flex flex-col gap-3">
                    <div className="flex gap-3 mini:flex-col md:flex-row">
                        <input
                            name="name"
                            type="text"
                            value={clientDraft.consultante.name}
                            onChange={handleClientNameDate}
                            placeholder="Nombre del Consultante"
                            className="h-10 pl-3 text-base text-gray-800 bg-white rounded-lg mini:w-full md:w-1/2 focus-visible:outline-none focus-visible:border-violetDark focus-visible:border-2"
                        />
                        <div className="flex gap-3 flex-1">
                            <input
                                type="date"
                                name="date"
                                value={clientDraft.consultante.date}
                                onChange={handleClientNameDate}
                                className="w-1/2 h-10 pl-3 text-base text-gray-800 bg-white rounded-lg focus-visible:outline-none focus-visible:border-violetDark focus-visible:border-2"
                            />
                            <input
                                type="text"
                                name="zodiac"
                                value={formatSignAndAge(clientDraft.consultante.zodiac, clientDraft.consultante.date)}
                                readOnly
                                disabled
                                className="w-1/2 h-10 px-3 text-base text-gray-800 bg-white rounded-lg disabled:bg-white disabled:text-gray-800"
                            />
                        </div>
                        <div className="flex items-center gap-1 text-white">
                            <button
                                type="button"
                                className="p-2 rounded-full bg-violetDark hover:cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed"
                                onClick={copiar}
                                title="Copiar"
                                disabled={
                                    clientDraft.consultante.name.length === 0 ||
                                    clientDraft.consultante.zodiac.length === 0
                                }
                            >
                                <ContentCopyIcon />
                            </button>
                            <button
                                type="button"
                                className="p-2 rounded-full bg-violetDark hover:cursor-pointer"
                                onClick={pegar}
                                title="Pegar"
                            >
                                <ContentPasteIcon />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto grid mini:grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-violetLight/70 shadow-card-horizontal">
                    <p className="mb-3 font-semibold text-white drop-shadow">Lecturas disponibles</p>
                    <div className="flex flex-col gap-1 max-h-[60vh] overflow-y-auto">
                        {FLEXIBLE_SPREADS.map((s) => {
                            const checked = selectedIds.includes(s.id)
                            const disabled = !checked && selectedIds.length >= COMBO_MAX_STEPS
                            return (
                                <label
                                    key={s.id}
                                    className={`flex items-center gap-2 p-2 rounded cursor-pointer text-white ${
                                        disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/20'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={() => toggleSpread(s.id)}
                                    />
                                    <span className="text-sm drop-shadow">
                                        {s.title}{' '}
                                        <span className="text-xs text-white/80">({s.cardCount} cartas)</span>
                                    </span>
                                </label>
                            )
                        })}
                    </div>
                </div>

                <div className="p-4 rounded-lg bg-violetLight/70 shadow-card-horizontal">
                    <p className="mb-3 font-semibold text-white drop-shadow">
                        Orden del combo ({selectedIds.length}/{COMBO_MAX_STEPS})
                    </p>
                    {selectedIds.length === 0 ? (
                        <p className="text-sm text-white drop-shadow">
                            Seleccioná al menos {COMBO_MIN_STEPS} lecturas.
                        </p>
                    ) : (
                        <ol className="flex flex-col gap-2">
                            {selectedIds.map((id, index) => {
                                const s = getFlexibleSpread(id)!
                                return (
                                    <li
                                        key={id}
                                        className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg"
                                    >
                                        <span className="text-sm">
                                            {index + 1}. {s.shortTitle}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                className="min-w-[40px] min-h-[40px] text-xl font-bold rounded-lg bg-violetDark text-white disabled:opacity-40"
                                                disabled={index === 0}
                                                onClick={() => move(index, -1)}
                                                title="Subir"
                                            >
                                                ↑
                                            </button>
                                            <button
                                                type="button"
                                                className="min-w-[40px] min-h-[40px] text-xl font-bold rounded-lg bg-violetDark text-white disabled:opacity-40"
                                                disabled={index === selectedIds.length - 1}
                                                onClick={() => move(index, 1)}
                                                title="Bajar"
                                            >
                                                ↓
                                            </button>
                                            <button
                                                type="button"
                                                className="px-3 py-2 text-sm rounded-lg bg-gray-300"
                                                onClick={() => toggleSpread(id)}
                                            >
                                                Quitar
                                            </button>
                                        </div>
                                    </li>
                                )
                            })}
                        </ol>
                    )}

                    <div className="flex justify-center mt-6">
                        <ButtonNormal
                            caption="Comenzar combo"
                            onClick={() => setStarted(true)}
                            disabled={!canStart}
                        />
                    </div>
                    {!canStart && selectedIds.length > 0 && selectedIds.length < COMBO_MIN_STEPS && (
                        <p className="mt-3 px-3 py-2 text-sm text-center text-red-700 bg-white rounded-lg font-medium">
                            Faltan {COMBO_MIN_STEPS - selectedIds.length} lectura(s) para armar el combo.
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

export default FlexibleCombo
